import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';
import axiosInstance from '../utils/axiosConfig';
import { tokenManager, userManager } from '../utils/auth';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // 초기 로드 시 토큰 확인
  useEffect(() => {
    const initAuth = async () => {
      const token = tokenManager.getToken();
      
      if (token && tokenManager.isTokenValid()) {
        const savedUser = userManager.getUser();
        if (savedUser) {
          setUser(savedUser);
          // axios 기본 헤더에 토큰 설정
          axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        }
      } else {
        // 토큰이 없거나 유효하지 않으면 정리
        userManager.logout();
      }
      
      setLoading(false);
    };

    initAuth();
  }, []);

  // 로그인 (username 기반으로 변경)
  const login = async (usernameOrEmail, password) => {
    try {
      setError(null);
      setLoading(true);

      const response = await axiosInstance.post(`/login`, {
        username: usernameOrEmail,
        password
      });

      const { access_token, user } = response.data;

      // 토큰과 사용자 정보 저장
      tokenManager.setToken(access_token);
      userManager.setUser(user);
      
      // axios 기본 헤더에 토큰 설정
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      
      setUser(user);
      setLoading(false);
      
      return { success: true };
    } catch (err) {
      setError(err.response?.data?.error || '로그인에 실패했습니다.');
      setLoading(false);
      return { 
        success: false, 
        error: err.response?.data?.error || '로그인에 실패했습니다.' 
      };
    }
  };

  // 회원가입
  const signup = async (userData) => {
    try {
      setError(null);
      setLoading(true);

      const response = await axiosInstance.post(`/auth/signup`, userData);

      const { token, user } = response.data;

      // 회원가입 성공 시 자동 로그인
      tokenManager.setToken(token);
      userManager.setUser(user);
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      
      setUser(user);
      setLoading(false);
      
      return { success: true };
    } catch (err) {
      setError(err.response?.data?.message || '회원가입에 실패했습니다.');
      setLoading(false);
      return { 
        success: false, 
        error: err.response?.data?.message || '회원가입에 실패했습니다.' 
      };
    }
  };

  // 로그아웃
  const logout = async () => {
    try {
      // 서버에 로그아웃 요청 (선택적)
      await axiosInstance.post(`/logout`);
    } catch (err) {
      // 로그아웃 요청 실패해도 로컬 정리는 진행
    } finally {
      // 로컬 스토리지 정리
      userManager.logout();
      
      // axios 헤더 정리
      delete axios.defaults.headers.common['Authorization'];
      
      setUser(null);
      setError(null);
    }
  };

  // 사용자 정보 업데이트
  const updateUser = async (updatedData) => {
    try {
      setError(null);
      setLoading(true);

      const response = await axiosInstance.post(`/users/${user.id}`, updatedData);
      
      const updatedUser = response.data.user;
      userManager.setUser(updatedUser);
      setUser(updatedUser);
      
      setLoading(false);
      return { success: true };
    } catch (err) {
      setError(err.response?.data?.message || '사용자 정보 업데이트에 실패했습니다.');
      setLoading(false);
      return { 
        success: false, 
        error: err.response?.data?.message || '사용자 정보 업데이트에 실패했습니다.' 
      };
    }
  };

  // 비밀번호 변경
  const changePassword = async (currentPassword, newPassword) => {
    try {
      setError(null);
      
      await axiosInstance.post(`/users/${user.id}/password`, {
        currentPassword,
        newPassword
      });
      
      return { success: true };
    } catch (err) {
      setError(err.response?.data?.message || '비밀번호 변경에 실패했습니다.');
      return { 
        success: false, 
        error: err.response?.data?.message || '비밀번호 변경에 실패했습니다.' 
      };
    }
  };

  const value = {
    user,
    loading,
    error,
    login,
    signup,
    logout,
    updateUser,
    changePassword,
    isAuthenticated: !!user
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};